<script lang="ts">
	import BigNumber from 'bignumber.js';
	import { getNumBackInUnits } from '../../util/util';
	import { getDisplayName } from '$lib/perps';
	import { marketPrices } from '$lib/util/marketHelper';
	import { popupModalData } from '$lib/popups/popupsStore';
	import { onMount } from 'svelte';
	import { userMarginList } from '../userInteract/store';
	import { positions } from '../store';
	import { addDollarSign } from '$lib/util/util.js';
	import type { Position } from '$lib/types/position';
	import type { UserMargins } from '$lib/types/user';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import domtoimage from 'dom-to-image';
	import ProcessingButton from '$lib/util/ProcessingButton.svelte';
	import ShareablePnlCheckbox from './ShareablePnlCheckbox.svelte';
	import { marketsConfig } from '$lib/config/markets';

	export let marketIdentifier: string;

	let type: string;
	let market: string;
	let pnl: BigNumber;
	let pnlAmount: string;
	let entryPrice: string;
	let exitPrice: string;
	let leverage: string;
	let asset: string;

	let showPnlAmount = true;
	let showPrice = true;
	let showLeverage = true;

	let tweetUuid = String(Math.floor(Math.random() * 100000) + 1);

	let COPY_IMAGE = 'Copy';
	function handleSuccessCopyImage() {
		COPY_IMAGE = 'Copied!';
		setTimeout(() => {
			COPY_IMAGE = 'Copy';
		}, 1000);
	}

	onMount(() => {
		return () => {
			popupModalData.set(undefined);
		};
	});

	$: {
		if (marketIdentifier && $positions && $positions.data && userMarginList) {
			const position: Position | undefined = $positions.data?.find(
				(pos) => pos.marketIdentifier === marketIdentifier
			);
			if (position) {
				type = new BigNumber(position.size).gt(0) ? 'Long' : 'Short';
				market = getDisplayName(position.marketIdentifier);
				asset = position.asset;
				pnlAmount = new BigNumber(
					new BigNumber($marketPrices[position.marketIdentifier]).minus(position.avgEntryPrice)
				)
					.multipliedBy(position.size)
					.div(1e18)
					.toFixed(2);
				const oraclePrice: string = $marketPrices[position.marketIdentifier];
				pnl = new BigNumber(pnlAmount).div(new BigNumber(position.margin)).multipliedBy(100);
				entryPrice = getNumBackInUnits(
					position.avgEntryPrice,
					18,
					marketsConfig[asset].showDecimals
				);
				const _exitPrice: BigNumber = new BigNumber(oraclePrice);
				exitPrice = getNumBackInUnits(_exitPrice.toString(), 18, marketsConfig[asset].showDecimals);
				const margin: UserMargins | undefined = $userMarginList.find(
					(margin) => margin.marketIdentifier == position.marketIdentifier
				);
				if (margin) {
					leverage = new BigNumber(position.size)
						.multipliedBy(oraclePrice)
						.div(1e18)
						.dividedBy(new BigNumber(margin.depositedMargin))
						.abs()
						.toNumber()
						.toFixed(2);
				}
			}
		}
	}
	function downloadImage() {
		var node = document.getElementById('shareable-pnl')!;

		let scale = 1600 / node.clientWidth;
		domtoimage
			.toBlob(node, {
				quality: 0.99,
				width: node.clientWidth * scale,
				height: node.clientHeight * scale,
				style: {
					transform: `scale(${scale})`,
					'transform-origin': 'top left'
				}
			})
			.then(function (blob: any) {
				var link = window.URL.createObjectURL(blob);
				// window.location = link;
				var a = document.createElement('a');
				document.body.appendChild(a);
				a.style = 'display: none';
				a.href = link;
				a.download = 'polynomial_share.png';
				a.click();
				window.URL.revokeObjectURL(link);
			});
	}

	async function getImageBlob() {
		try {
			var node = document.getElementById('shareable-pnl')!;

			let scale = 1600 / node.clientWidth;
			const blob = domtoimage.toBlob(node, {
				quality: 0.99,
				width: node.clientWidth * scale,
				height: node.clientHeight * scale,
				style: {
					transform: `scale(${scale})`,
					'transform-origin': 'top left'
				}
			});
			return blob;
		} catch (err) {
			console.log(err);
		}
	}

	async function copyImage() {
		try {
			// copy image to clipboard
			const imageBlob = await getImageBlob();
			const data = [new ClipboardItem({ 'image/png': imageBlob })];
			navigator.clipboard.write(data);
			handleSuccessCopyImage();
		} catch (err) {
			console.log(err);
		}
	}

	async function tweet(uuid: string) {
		const imageBlob = await getImageBlob();
		const formData = new FormData();
		formData.append('image', imageBlob);
		formData.append('uuid', uuid);
		fetch('/api/share', {
			method: 'POST',
			body: formData
		});
		tweetUuid = String(Math.floor(Math.random() * 100000) + 1);
	}

	$: tweetText =
		pnl && asset && market && leverage
			? `I just gained ${pnl.toFixed(2)}% on $${market} on @PolynomialFi trade.`
			: 'Checkout my PnL on @PolynomialFi trade';
</script>

<GradientBorder
	gradient="bg-fx-gradient-6"
	paddingClass="p-px"
	borderRadius="rounded-2xl squircle-2xl"
	outerClassList="h-fit w-full sm:w-[64vw] lg:w-[52vw] xl:w-[42vw] 3xl:w-[35vw] z-50 relative p-6 bg-fx-gradient-6 rounded-2xl squircle-2xl"
	innerClassList="bg-fx-black"
>
	<div class="w-full bg-fx-gradient-8 rounded-2xl squircle-2xl">
		<div class="bg-fx-black aspect-[16/9] rounded-2xl squircle-2xl" id="shareable-pnl">
			<div
				class="flex justify-between items-center w-full bg-fx-gradient-8 overflow-clip relative h-full rounded-2xl squircle-2xl"
			>
				<div class="flex-col px-8 py-8 sm:px-12 sm:py-12 z-10">
					<img
						loading="lazy"
						src={'shareable/polynomial_logo.png'}
						alt="logo"
						class="h-14 mb-5 3xl:h-24"
					/>
					<div class="flex items-center gap-5 sm:gap-4 3xl:gap-20">
						<div class="text-fx-grey-300 text-xs sm:text-base 3xl:text-3xl font-semibold">
							{market}
						</div>

						{#if type === 'Short'}
							<img class="h-5 sm:h-7 3xl:h-14" loading="lazy" src={'short-tab.png'} alt="short" />
						{:else}
							<img class="h-5 sm:h-7 3xl:h-14" loading="lazy" src={'long-tab.png'} alt="long" />
						{/if}
						{#if showLeverage}
							{#if leverage}
								<div class="text-fx-grey-300 text-xs sm:text-base 3xl:text-3xl font-semibold">
									{leverage}x
								</div>
							{:else}
								<div
									class="animate-pulse bg-fx-grey-700 h-6 w-9 col-span-2 rounded-2xl squircle-2xl"
								/>
							{/if}
						{/if}
					</div>
					<div
						class="flex justify-start text-2xl sm:text-5xl 3xl:text-8xl font-semibold pt-4 sm:pt-6 items-end"
						class:text-fx-loss={pnl.lt(0)}
						class:text-fx-profit={pnl.gte(0)}
					>
						<div>
							{pnl.gt(0) ? '+' : '-'}
							{pnl.abs().toFixed(2)}%
						</div>
						{#if showPnlAmount}
							<div class="text-base sm:text-2xl 3xl:text-5xl ml-4 sm:mb-3 mb-0.5">
								({addDollarSign(getNumBackInUnits(pnlAmount, 18, 2))})
							</div>
						{/if}
					</div>
					{#if showPrice}
						<div class="flex gap-3 sm:pt-6 pt-3 sm:text-lg text-xs 3xl:text-3xl">
							<div class=" text-fx-grey-300">
								<div>Entry Price:</div>
							</div>
							<div class="text-fx-white">
								<div>{addDollarSign(entryPrice, marketsConfig[asset].showDecimals)}</div>
							</div>
						</div>
						<div class="flex gap-3 sm:pt-px sm:text-lg text-xs 3xl:text-3xl">
							<div class=" text-fx-grey-300">
								<div>Mark Price:</div>
							</div>
							<div class="text-fx-white">
								<div>{addDollarSign(exitPrice, marketsConfig[asset].showDecimals)}</div>
							</div>
						</div>
					{/if}
				</div>
				<img
					loading="lazy"
					src={'shareable/ship.png'}
					alt="logo"
					class="absolute right-0 top-0 h-full"
				/>
			</div>
		</div>
		<GradientBorder
			gradient="bg-fx-gradient-6"
			paddingClass="p-px"
			borderRadius="rounded-2xl squircle-2xl"
			outerClassList=" mx-5"
			innerClassList="bg-fx-black"
		>
			<div
				class="w-full p-5 bg-fx-gradient-8 bg-opacity-10 backdrop-blur-xl rounded-2xl squircle-2xl"
			>
				<div class="text-fx-grey-400 text-sm">Optional information to share</div>
				<div class="mt-3 flex text-fx-grey-400 text-sm justify-start w-full gap-7">
					<ShareablePnlCheckbox bind:bindingVal={showPnlAmount} title="Show PnL Amount" />
					<ShareablePnlCheckbox bind:bindingVal={showLeverage} title="Show Leverage" />
				</div>
			</div>
		</GradientBorder>
		<div class="grid grid-cols-3 py-3 3xl:py-6 px-5 gap-5 3xl:gap-10">
			{#if showLeverage && !leverage}
				<ProcessingButton title="Download" />
			{:else}
				<GradientBorder
					gradient="bg-fx-gradient-6"
					paddingClass="p-px"
					borderRadius="rounded-2xl squircle-2xl"
					outerClassList=""
					innerClassList="bg-fx-black"
				>
					<div
						class="bg-fx-gradient-8 text-center py-4 3xl:py-6 font-semibold text-fx-white cursor-pointer text-xs sm:text-sm rounded-2xl squircle-2xl flex justify-center gap-2"
						on:click|stopPropagation={downloadImage}
					>
						<img class="w-4" loading="lazy" src={'download.svg'} alt="download" />
						Download
					</div>
				</GradientBorder>
			{/if}

			<GradientBorder
				gradient="bg-fx-gradient-6"
				paddingClass="p-px"
				borderRadius="rounded-2xl squircle-2xl"
				outerClassList=""
				innerClassList="bg-fx-black"
			>
				<div
					class="bg-fx-gradient-8 text-center py-4 3xl:py-6 font-semibold text-fx-white cursor-pointer text-xs sm:text-sm rounded-2xl squircle-2xl flex justify-center gap-2 items-center"
					on:click|stopPropagation={copyImage}
				>
					<img class="w-4 h-4" loading="lazy" src={'scwallet/copy.png'} alt="copy" />

					{COPY_IMAGE}
				</div>
			</GradientBorder>

			<GradientBorder
				gradient="bg-fx-gradient-9"
				paddingClass="p-px"
				borderRadius="rounded-2xl squircle-2xl"
				outerClassList=""
				innerClassList="bg-fx-black"
			>
				<div
					class="bg-fx-gradient-8 text-center py-4 3xl:py-6 font-semibold text-fx-white cursor-pointer text-xs sm:text-sm rounded-2xl squircle-2xl"
					on:click|stopPropagation={() => tweet(tweetUuid)}
				>
					<a
						href="https://twitter.com/intent/tweet?text={encodeURIComponent(
							tweetText
						)}&url=https://trade.polynomial.fi/share-{tweetUuid}"
						target="_blank"
					>
						Tweet
					</a>
				</div>
			</GradientBorder>
		</div>
	</div>
</GradientBorder>
